import { useAuth } from "@/lib/auth-context";
import { cn } from "@/lib/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState } from "react";

// ============================================================
// Password Change + Profile Info
// ============================================================

export function PasswordSection() {
	const { user } = useAuth();
	const queryClient = useQueryClient();
	const [current, setCurrent] = useState("");
	const [newPw, setNewPw] = useState("");
	const [confirm, setConfirm] = useState("");
	const [success, setSuccess] = useState(false);

	const mutation = useMutation({
		mutationFn: (data: { currentPassword: string; newPassword: string }) =>
			import("@/lib/api").then((m) => m.api.put("/auth/password", data)),
		onSuccess: () => {
			setCurrent("");
			setNewPw("");
			setConfirm("");
			setSuccess(true);
			setTimeout(() => setSuccess(false), 3000);
			queryClient.invalidateQueries({ queryKey: ["auth"] });
		},
	});

	function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (newPw !== confirm) return;
		if (newPw.length < 8) return;
		mutation.mutate({ currentPassword: current, newPassword: newPw });
	}

	const mismatch = confirm.length > 0 && newPw !== confirm;

	return (
		<div className="space-y-3">
			<h2 className="font-medium text-stone-700">Profil</h2>
			<div className="card max-w-md space-y-4">
				<div>
					<p className="text-sm font-medium text-stone-700">{user?.name}</p>
					<p className="text-xs text-stone-400">{user?.email}</p>
				</div>

				<form onSubmit={handleSubmit} className="space-y-3">
					<div>
						<label
							htmlFor="pw-current"
							className="text-xs font-medium text-stone-500 block mb-1"
						>
							Aktuelles Passwort
						</label>
						<input
							id="pw-current"
							type="password"
							value={current}
							onChange={(e) => setCurrent(e.target.value)}
							className="input"
							autoComplete="current-password"
						/>
					</div>
					<div>
						<label
							htmlFor="pw-new"
							className="text-xs font-medium text-stone-500 block mb-1"
						>
							Neues Passwort
						</label>
						<input
							id="pw-new"
							type="password"
							value={newPw}
							onChange={(e) => setNewPw(e.target.value)}
							className="input"
							autoComplete="new-password"
							minLength={8}
						/>
					</div>
					<div>
						<label
							htmlFor="pw-confirm"
							className="text-xs font-medium text-stone-500 block mb-1"
						>
							Neues Passwort bestätigen
						</label>
						<input
							id="pw-confirm"
							type="password"
							value={confirm}
							onChange={(e) => setConfirm(e.target.value)}
							className={cn("input", mismatch && "border-red-300")}
							autoComplete="new-password"
						/>
						{mismatch && (
							<p className="text-xs text-red-500 mt-1">
								Passwörter stimmen nicht überein
							</p>
						)}
					</div>

					{mutation.isError && (
						<p className="text-xs text-red-600">
							{mutation.error instanceof Error
								? mutation.error.message
								: "Fehler beim Ändern"}
						</p>
					)}

					{success && (
						<p className="text-xs text-green-600">
							Passwort erfolgreich geändert.
						</p>
					)}

					<button
						type="submit"
						disabled={
							mutation.isPending ||
							!current ||
							newPw.length < 8 ||
							newPw !== confirm
						}
						className="btn-primary text-sm disabled:opacity-40"
					>
						{mutation.isPending ? (
							<Loader2 size={14} className="animate-spin inline mr-1" />
						) : null}
						Passwort ändern
					</button>
				</form>
			</div>
		</div>
	);
}
